(function() {
  'use strict';

  var CartRewardBar = (function() {
    var instance = null;
    var boundHandlers = {};
    var debounceTimers = {};
    var _fetchPromise = null;
    var _cartCache = null;
    var _cartCacheTime = 0;

    var state = {
      version: 0,
      pendingVersion: 0,
      lastVerifiedCount: 0,
      currentDisplayCount: 0,
      lastItems: [],
      isInitialized: false,
      isUpdating: false
    };

    var config = {
      milestones: [],
      freeShippingThreshold: 2,
      freeGiftThreshold: 5,
      freeGiftVariantId: null,
      maxThreshold: 2
    };

    var domCache = {
      element: null,
      progressBar: null,
      messageEl: null,
      milestones: null
    };

    function getInstance() {
      if (!instance) {
        instance = createInstance();
      }
      return instance;
    }

    function createInstance() {
      return {
        init: init,
        destroy: destroy,
        getState: function() { return Object.assign({}, state); },
        updateFromCart: updateFromCart,
        _forceUpdate: forceUpdate
      };
    }

    function init(element) {
      if (state.isInitialized) {
        return;
      }

      if (!element) {
        element = document.querySelector('[data-cart-reward-bar]');
      }

      if (!element) {
        return;
      }

      domCache.element = element;
      domCache.progressBar = element.querySelector('[data-reward-progress]');
      domCache.messageEl = element.querySelector('[data-reward-message]');
      domCache.milestones = element.querySelectorAll('[data-milestone]');

      if (!domCache.progressBar || !domCache.messageEl) {
        return;
      }

      parseConfig(element);

      var initialCount = parseInt(element.dataset.cartItemCount, 10) || 0;
      state.lastVerifiedCount = initialCount;
      state.currentDisplayCount = initialCount;

      bindEvents();
      state.isInitialized = true;

      render(initialCount);
    }

    function parseConfig(element) {
      config.freeShippingThreshold = parseInt(element.dataset.freeShippingCount, 10) || 2;
      config.freeGiftThreshold = parseInt(element.dataset.freeGiftCount, 10) || 5;
      config.freeGiftVariantId = normalizeVariantId(element.dataset.freeGiftProductId);
      config.freeGiftEnabled = element.dataset.freeGiftEnable === 'true';
      config.maxThreshold = parseInt(element.dataset.maxThreshold, 10) || config.freeShippingThreshold;

      config.milestones = [];

      config.milestones.push({
        type: 'shipping',
        threshold: config.freeShippingThreshold,
        reached: false
      });

      if (config.freeGiftEnabled) {
        config.milestones.push({
          type: 'gift',
          threshold: config.freeGiftThreshold,
          variantId: config.freeGiftVariantId,
          reached: false
        });
      }

      config.milestones.sort(function(a, b) {
        return a.threshold - b.threshold;
      });

      config.maxThreshold = config.milestones[config.milestones.length - 1].threshold;
    }

    function normalizeVariantId(id) {
      if (!id) return null;
      var strId = String(id);
      if (strId.indexOf('gid://') === 0) {
        var parts = strId.split('/');
        return parts[parts.length - 1];
      }
      return strId;
    }

    function bindEvents() {
      boundHandlers.onCartUpdate = function(e) {
        handleCartUpdate(e);
      };

      boundHandlers.onCartRendered = function(e) {
        handleCartRendered(e);
      };

      boundHandlers.onProductAdded = function(e) {
        handleProductAdded(e);
      };

      boundHandlers.onDrawerOpen = function() {
        handleDrawerOpen();
      };

      boundHandlers.onUpsellAdded = function(e) {
        handleUpsellAdded(e);
      };

      document.addEventListener('cart:updated', boundHandlers.onCartUpdate);
      document.addEventListener('cart:rendered', boundHandlers.onCartRendered);
      document.addEventListener('ajaxProduct:added', boundHandlers.onProductAdded);
      document.addEventListener('drawerOpen', boundHandlers.onDrawerOpen);
      document.addEventListener('drawerOpen.CartDrawer', boundHandlers.onDrawerOpen);
      document.addEventListener('cart:open', boundHandlers.onDrawerOpen);
      document.addEventListener('upsell:product:added', boundHandlers.onUpsellAdded);
    }

    function unbindEvents() {
      document.removeEventListener('cart:updated', boundHandlers.onCartUpdate);
      document.removeEventListener('cart:rendered', boundHandlers.onCartRendered);
      document.removeEventListener('ajaxProduct:added', boundHandlers.onProductAdded);
      document.removeEventListener('drawerOpen', boundHandlers.onDrawerOpen);
      document.removeEventListener('drawerOpen.CartDrawer', boundHandlers.onDrawerOpen);
      document.removeEventListener('cart:open', boundHandlers.onDrawerOpen);
      document.removeEventListener('upsell:product:added', boundHandlers.onUpsellAdded);
      boundHandlers = {};
    }

    function debounce(key, fn, delay) {
      if (debounceTimers[key]) {
        clearTimeout(debounceTimers[key]);
      }
      debounceTimers[key] = setTimeout(function() {
        delete debounceTimers[key];
        fn();
      }, delay || 100);
    }

    function scheduleCartValidation(delay) {
      debounce('cartValidate', function() {
        fetchFreshCart().then(function(cart) {
          if (!cart) return;
          state.lastVerifiedCount = cart.item_count;
          state.currentDisplayCount = cart.item_count;
          state.lastItems = cart.items || [];
          render(cart.item_count, cart.items || []);
        });
      }, delay || 250);
    }

    function invalidateLocalCache() {
      _cartCache = null;
      _cartCacheTime = 0;
    }

    function handleCartUpdate(e) {
      var cart = e && e.detail && e.detail.cart;
      if (cart && typeof cart.item_count !== 'undefined') {
        state.version = Date.now();
        state.lastVerifiedCount = cart.item_count;
        state.currentDisplayCount = cart.item_count;
        state.lastItems = cart.items || [];

        _cartCache = cart;
        _cartCacheTime = Date.now();

        render(cart.item_count, cart.items || []);
      }
    }

    function handleCartRendered(e) {
      if (!e || !e.detail) return;

      var count = e.detail.count;
      if (typeof count === 'undefined') return;

      state.version = Date.now();
      state.lastVerifiedCount = count;
      state.currentDisplayCount = count;

      render(count, state.lastItems);

      invalidateLocalCache();
      scheduleCartValidation(400);
    }

    function handleProductAdded(e) {
      if (e && e.detail && (e.detail._rewardBarInternal || e.detail._upsellInternal)) {
        return;
      }

      var quantity = 1;
      if (e && e.detail && e.detail.quantity) {
        quantity = e.detail.quantity;
      }

      optimisticUpdate(quantity);
    }

    function handleUpsellAdded(e) {
      var quantity = (e && e.detail && e.detail.quantity) || 1;

      invalidateLocalCache();
      optimisticUpdate(quantity);
      scheduleCartValidation(300);
    }

    function handleDrawerOpen() {
      if (!state.isInitialized) {
        var el = document.querySelector('[data-cart-reward-bar]');
        if (el) {
          init(el);
        } else {
          return;
        }
      }

      invalidateLocalCache();
      scheduleCartValidation(150);
    }

    function optimisticUpdate(delta) {
      state.pendingVersion = Date.now();
      state.currentDisplayCount = Math.max(0, state.currentDisplayCount + delta);

      render(state.currentDisplayCount, state.lastItems);

      invalidateLocalCache();
      scheduleCartValidation(500);
    }

    function fetchFreshCart() {
      if (window.CartData && typeof window.CartData.getCart === 'function') {
        window.CartData.invalidateCache();
        return window.CartData.getCart();
      }

      if (_fetchPromise) {
        return _fetchPromise;
      }

      _fetchPromise = fetch('/cart.js?t=' + Date.now(), {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(function(response) {
          if (!response.ok) return null;
          return response.json();
        })
        .then(function(cart) {
          _fetchPromise = null;
          if (cart) {
            _cartCache = cart;
            _cartCacheTime = Date.now();
          }
          return cart || null;
        })
        .catch(function() {
          _fetchPromise = null;
          return null;
        });

      return _fetchPromise;
    }

    function forceUpdate() {
      invalidateLocalCache();
      Object.keys(debounceTimers).forEach(function(key) {
        clearTimeout(debounceTimers[key]);
      });
      debounceTimers = {};
      scheduleCartValidation(0);
    }

    function updateFromCart() {
      invalidateLocalCache();
      scheduleCartValidation(0);
    }

    function render(itemCount, items) {
      items = items || [];

      if (domCache.progressBar) {
        var percentage = Math.min(100, Math.max(0, (itemCount / config.maxThreshold) * 100));
        domCache.progressBar.style.width = percentage.toFixed(2) + '%';
      }

      updateMessage(itemCount);
      updateMilestones(itemCount, items);
    }

    function updateMessage(itemCount) {
      if (!domCache.messageEl) return;

      var message = '';
      var reachedMilestones = config.milestones.filter(function(m) {
        return itemCount >= m.threshold;
      });

      if (reachedMilestones.length === config.milestones.length) {
        message = getMessage('all_rewards_unlocked');
      } else if (reachedMilestones.length > 0) {
        var nextMilestone = config.milestones.find(function(m) {
          return itemCount < m.threshold;
        });
        if (nextMilestone) {
          var remaining = nextMilestone.threshold - itemCount;
          message = getMessage('add_more_for_reward', remaining, nextMilestone.type);
        } else {
          message = getMessage('all_rewards_unlocked');
        }
      } else {
        var firstMilestone = config.milestones[0];
        var remaining = firstMilestone.threshold - itemCount;
        message = getMessage('add_more_for_reward', remaining, firstMilestone.type);
      }

      domCache.messageEl.innerHTML = message;
    }

    function updateMilestones(itemCount, items) {
      if (!domCache.milestones) return;

      var giftVariantId = normalizeVariantId(config.freeGiftVariantId);
      var hasGift = false;

      if (giftVariantId && items.length > 0) {
        hasGift = items.some(function(item) {
          var itemVariantId = normalizeVariantId(item.variant_id);
          return itemVariantId === giftVariantId;
        });
      }

      domCache.milestones.forEach(function(milestoneEl) {
        var threshold = parseInt(milestoneEl.dataset.threshold, 10);
        var type = milestoneEl.dataset.milestone;
        var isReached = itemCount >= threshold;
        var icon = milestoneEl.querySelector('.cart-reward-bar__milestone-icon');

        var wasReached = milestoneEl.classList.contains('is-reached');

        if (isReached && !wasReached) {
          milestoneEl.classList.add('is-reached');
          if (icon) {
            triggerBounceAnimation(icon);
          }
        } else if (!isReached && wasReached) {
          milestoneEl.classList.remove('is-reached');
        }

        if (type === 'gift' && giftVariantId) {
          if (hasGift) {
            milestoneEl.classList.add('has-gift');
          } else {
            milestoneEl.classList.remove('has-gift');
          }
        }
      });

      updateContainerState(itemCount);
    }

    function updateContainerState(itemCount) {
      if (!domCache.element) return;

      config.milestones.forEach(function(milestone) {
        var className = 'cart-reward-bar--' + milestone.type + '-reached';
        if (itemCount >= milestone.threshold) {
          domCache.element.classList.add(className);
        } else {
          domCache.element.classList.remove(className);
        }
      });
    }

    function triggerBounceAnimation(icon) {
      if (!icon) return;

      icon.classList.remove('is-bouncing');

      requestAnimationFrame(function() {
        icon.classList.add('is-bouncing');
      });

      setTimeout(function() {
        icon.classList.remove('is-bouncing');
      }, 600);
    }

    function getMessage(key, count, type) {
      count = count || 0;
      type = type || 'shipping';

      var messages = getMessagesFromTheme();

      var keyMap = {
        'add_more_for_reward': {
          shipping: messages.addMoreForShipping || 'Add {{count}} more item(s) for FREE shipping!',
          gift: messages.addMoreForGift || 'Add {{count}} more item(s) for a FREE gift!'
        },
        'shipping_unlocked': messages.shippingUnlocked || "Congratulations! You've unlocked FREE shipping!",
        'all_rewards_unlocked': messages.allRewardsUnlocked || "Free shipping unlocked!"
      };

      var message = '';
      if (key === 'add_more_for_reward') {
        message = keyMap[key][type] || keyMap[key]['shipping'];
      } else {
        message = keyMap[key] || '';
      }

      if (count > 0 && message.indexOf('{{count}}') !== -1) {
        message = message.replace('{{count}}', count);
      }

      return message;
    }

    function getMessagesFromTheme() {
      if (window.theme && window.theme.strings) {
        return {
          addMoreForShipping: window.theme.strings.reward_add_more_items_for_free_shipping,
          addMoreForGift: window.theme.strings.reward_add_more_items_for_free_gift,
          shippingUnlocked: window.theme.strings.reward_congratulations_free_shipping,
          allRewardsUnlocked: window.theme.strings.reward_congratulations_all_rewards
        };
      }
      return {};
    }

    function destroy() {
      Object.keys(debounceTimers).forEach(function(key) {
        clearTimeout(debounceTimers[key]);
      });
      debounceTimers = {};
      _fetchPromise = null;
      _cartCache = null;
      _cartCacheTime = 0;

      if (state.isInitialized) {
        unbindEvents();
      }

      state = {
        version: 0,
        pendingVersion: 0,
        lastVerifiedCount: 0,
        currentDisplayCount: 0,
        lastItems: [],
        isInitialized: false,
        isUpdating: false
      };

      domCache = {
        element: null,
        progressBar: null,
        messageEl: null,
        milestones: null
      };

      instance = null;
    }

    return getInstance();
  })();

  function initCartRewardBar() {
    var rewardBar = document.querySelector('[data-cart-reward-bar]');
    if (rewardBar) {
      CartRewardBar.init(rewardBar);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartRewardBar);
  } else {
    initCartRewardBar();
  }

  window.CartRewardBar = CartRewardBar;
})();
